import React, { useEffect, useState } from 'react';
import { useAuditTrailService } from '../../services/audit-trails-service';
import IAuditTrail from '../../shared/IAuditTrail';
import Table from '../ui/Table/Table';
import Spinner from '../ui/Spinner/Spinner';
import Accordion from '../ui/Accordion/Accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import './AuditTrail.scss';

interface Props {
  kind: string;
  targetId: string;
}

const AuditTrailList: React.FC<Props> = ({ kind, targetId }) => {
  const [auditTrails, setAuditTrails] = useState<IAuditTrail[]>([]);
  const [loading, setLoading] = useState(true);
  const { fetchAuditTrails } = useAuditTrailService();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAuditTrails({ kind, targetId });
        setAuditTrails(data);
      } catch (error) {
        console.error("Failed to fetch audit trails", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [kind, targetId, fetchAuditTrails]);

  if (loading) return <Spinner />;
  if (auditTrails.length === 0) return <p className="no-history-text">No history found.</p>;

  return (
    <div className="audit-trail-history-section">
      <h3 className="audit-trail-history-heading">History</h3>
      <div className="audit-trail-list-container">
        <div className="audit-trail-list-header">
          <div className="column-header">Action</div>
          <div className="column-header">Operator</div>
          <div className="column-header">Date</div>
          <div className="column-header">Changes</div>
        </div>
        <div className="audit-trail-list-body">
          {auditTrails.map((item) => {
            const hasChanges = item.changes && item.changes.length > 0;
            const rowHeader = (
              <div className="audit-trail-row-header">
                <div className="header-cell">
                  <span className={`action-tag action-${item.action.toLowerCase().replace('_', '-')}`}>
                    {item.action}
                  </span>
                </div>
                <div className="header-cell">
                  {item.actionUser?.name || item.actionUser?.username || item.actionUser?.email || <span className="system-operator">System</span>}
                </div>
                <div className="header-cell">{item.created}</div>
                <div className="header-cell changes-summary">
                  {hasChanges ? `${item.changes.length} changes` : '-'}
                </div>
              </div>
            );

            return (
              <div key={item.id} className="audit-trail-list-item">
                <Accordion text={rowHeader} withContentPadding>
                  {hasChanges ? (
                    <Table condensed style={{ margin: 0, width: '100%', borderTop: 0 }}>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Old Value</th>
                          <th style={{ textAlign: 'center' }}></th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.changes.map((change, index) => (
                          <tr key={index}>
                            <td style={{ fontWeight: 500 }}>{change.fieldName}</td>
                            <td>{change.oldValue || '-'}</td>
                            <td style={{ textAlign: 'center', color: '#ccc' }}>
                              <FontAwesomeIcon icon={faArrowRight} />
                            </td>
                            <td style={{ fontWeight: 500 }}>{change.newValue || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div style={{ padding: '1rem', color: '#888' }}>No changes recorded.</div>
                  )}
                </Accordion>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AuditTrailList;
